class SymbolSearchService {
  constructor() {
    this.urls = {
      NFO: "https://v2api.aliceblueonline.com/restpy/contract_master?exch=NFO",
      NSE: "https://v2api.aliceblueonline.com/restpy/contract_master?exch=NSE",
      BSE: "https://v2api.aliceblueonline.com/restpy/contract_master?exch=BSE",
      MCX: "https://v2api.aliceblueonline.com/restpy/contract_master?exch=MCX",
      BSE: "https://v2api.aliceblueonline.com/restpy/contract_master?exch=BSE",
      BFO: "https://v2api.aliceblueonline.com/restpy/contract_master?exch=BFO"
    };
    this.allSymbolData = [];
    this.isInitialized = false;
    this.dbName = 'SymbolSearchDB';
    this.dbVersion = 1;
    this.storeName = 'contractData';
  }

  // Initialize IndexedDB
  async _initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('exchange', 'exchange', { unique: false });
          store.createIndex('lastUpdated', 'lastUpdated', { unique: false });
          store.createIndex('source', 'source', { unique: false });
        }
      };
    });
  }

  // Checking if data is from today
  _isToday(timestamp) {
    if (!timestamp) return false;
    const lastUpdated = new Date(timestamp);
    const now = new Date();
    return (
      lastUpdated.getFullYear() === now.getFullYear() &&
      lastUpdated.getMonth() === now.getMonth() &&
      lastUpdated.getDate() === now.getDate()
    );
  }

  // Getting data from IndexedDB with date check
  async _getStoredData(exchange) {
    try {
      const db = await this._initDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('exchange');
      
      const request = index.getAll(exchange);
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const results = request.result;
          if (results.length === 0) {
            resolve(null);
            return;
          }
          
          // Check if any data is outdated
          const hasOutdatedData = results.some(result => !this._isToday(result.lastUpdated));
          if (hasOutdatedData) {
            // If any data is outdated, remove all entries for this exchange
            this._removeStoredData(exchange);
            resolve(null);
            return;
          }
          
          // Merge all data from different sources for this exchange
          let mergedData = [];
          results.forEach(result => {
            if (result.data && Array.isArray(result.data)) {
              mergedData = [...mergedData, ...result.data];
            }
          });
          
          if (mergedData.length === 0) {
            resolve(null);
            return;
          }
          
          // Remove duplicates based on trading_symbol and token
          const uniqueData = mergedData.filter((item, index, self) => 
            index === self.findIndex(t => 
              t.trading_symbol === item.trading_symbol && 
              t.token === item.token
            )
          );
          
          // console.log(`${exchange} merged data from ${results.length} sources: ${uniqueData.length} unique records`);
          resolve(uniqueData);
        };
        
        request.onerror = () => {
          console.error(`Error reading stored data for ${exchange}:`, request.error);
          resolve(null);
        };
      });
    } catch (error) {
      console.error(`Error reading stored data for ${exchange}:`, error);
      return null;
    }
  }

  // Storing data in IndexedDB with metadata
  async _storeData(exchange, data, source = null) {
    try {
      const db = await this._initDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      // Create unique key to prevent overwriting
      const key = source ? `${exchange}_${source}` : `${exchange}_direct`;
      
      // Always replace existing data instead of appending
      const record = {
        key: key,
        exchange: exchange,
        data: data,
        lastUpdated: new Date().toISOString(),
        recordCount: data.length,
        source: source || 'direct'
      };
      
      return new Promise((resolve, reject) => {
        // Use put to either add new record or replace existing one
        const putRequest = store.put(record);
        putRequest.onsuccess = () => {
          // console.log(`${exchange} data stored/replaced in IndexedDB (source: ${source || 'direct'}). Total records: ${data.length}`);
          resolve();
        };
        putRequest.onerror = () => {
          console.error(`Error storing data for ${exchange}:`, putRequest.error);
          reject(putRequest.error);
        };
      });
    } catch (error) {
      console.error(`Error storing data for ${exchange}:`, error);
      throw error;
    }
  }

  // Remove stored data for an exchange
  async _removeStoredData(exchange) {
    try {
      const db = await this._initDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('exchange');
      
      const request = index.getAllKeys(exchange);
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const keys = request.result;
          keys.forEach(key => {
            store.delete(key);
          });
          // console.log(`Removed ${keys.length} data entries for ${exchange}`);
          resolve();
        };
        
        request.onerror = () => {
          console.error(`Error removing stored data for ${exchange}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`Error removing stored data for ${exchange}:`, error);
    }
  }

  // Downloading data for a specific exchange
  async downloadExchangeData(exchange) {
    const storedData = await this._getStoredData(exchange);
    if (storedData) {
      // console.log(`${exchange} is already up to date.`);
      return storedData;
    }

    try {
      // console.log(`Downloading ${exchange} data...`);
      const response = await fetch(this.urls[exchange]);

      if (!response.ok) {
        throw new Error(
          `Failed to download ${exchange} data: ${response.status}`
        );
      }

      const data = await response.json();

      let extractedData = [];
      
      // Special handling for INDICES which has nested BSE and NSE keys
      if (exchange === "INDICES") {

        if (data.BSE && data.NSE) {
          // Process BSE data and set segment_type to bse_idx
          const processedBSE = data.BSE.map(item => ({
            ...item,
            exchange_segment: "bse_idx",
            exch: "BSE",
            trading_symbol: item.symbol,
            token: item.token?.toString(),
            formatted_ins_name: item.symbol,
          }));
          
          // Process NSE data and set segment_type to nse_idx
          const processedNSE = data.NSE.map(item => ({
            ...item,
            exchange_segment: "nse_idx",
            exch: "NSE",
            trading_symbol: item.symbol,
            token: item.token.toString(),
            formatted_ins_name: item.symbol,
          }));
          
          // Store processed BSE data to BSE storage
          if (processedBSE.length > 0) {
            await this._storeData("BSE", processedBSE, "indices");
            // console.log(`INDICES BSE data stored: ${processedBSE.length} records with segment_type: bse_idx`);
          }
          
          // Store processed NSE data to NSE storage
          if (processedNSE.length > 0) {
            await this._storeData("NSE", processedNSE, "indices");
            // console.log(`INDICES NSE data stored: ${processedNSE.length} records with segment_type: nse_idx`);
          }
          
          // Return combined processed data for immediate use
          extractedData = [...processedBSE, ...processedNSE];
          // console.log(`INDICES data distributed: BSE records: ${processedBSE.length}, NSE records: ${processedNSE.length}`);
        } else {
          console.warn("INDICES response missing BSE or NSE keys");
          extractedData = Array.isArray(data) ? data : [];
        }
      } else {
        // Standard handling for other exchanges
        if (data[exchange]) {
          extractedData = data[exchange];
        } else {
          console.warn(
            `Unexpected format: Missing key ${exchange} in response`
          );
          extractedData = Array.isArray(data) ? data : [];
        }
      }

      // Only store data for non-INDICES exchanges (INDICES data is already stored above)
      if (exchange !== "INDICES") {
        await this._storeData(exchange, extractedData);

      }

      return extractedData;
    } catch (error) {
      console.error(`Failed to download ${exchange} data:`, error);
      throw error;
    }
  }

  // Loading combined data from all exchanges
  async loadCombinedData() {
    if (this.isInitialized && this.allSymbolData.length > 0) {
      return this.allSymbolData;
    }

    // Clear all existing data from IndexedDB before loading new data
    await this.clearCache();

    const exchanges = Object.keys(this.urls);
    let allData = [];

    for (const exchange of exchanges) {
      try {
        // Skip INDICES as it's handled specially and data is distributed to BSE/NSE
        if (exchange === "INDICES") {
          const exchangeData = await this.downloadExchangeData(exchange);
          allData = [...allData, ...exchangeData];
          continue;
        }
        
        const exchangeData = await this.downloadExchangeData(exchange);
        allData = [...allData, ...exchangeData];
      } catch (error) {
        console.error(`Error loading data for ${exchange}:`, error);
      }
    }

    this.allSymbolData = allData;
    this.isInitialized = true;

    
    // Debug cache state after loading
    await this.debugCache();
    
    return allData;
  }

  // Normalized formatted names (same as Flutter implementation)
  normalizeFormattedName(name) {
    const formattedNameExceptions = {
      "nifty bank": "BANKNIFTY",
      "nifty fin service": "FINNIFTY",
      "nifty midcap 100": "MIDCPNIFTY",
      "nifty next 50": "NIFTYNXT50",
      "nifty 50": "NIFTY",
    };

    const lowerName = name.toLowerCase().trim();
    return formattedNameExceptions[lowerName] || name;
  }

  // Searching symbols (same logic as Flutter implementation)
  searchSymbols(searchText) {
    if (!searchText || !this.allSymbolData.length) return [];

    const searchTokens = searchText.toLowerCase().trim().split(/\s+/);

    const symbols = this.allSymbolData
      .filter((item) => {
        const originalName = item.formatted_ins_name?.toString() || "";
        const name = this.normalizeFormattedName(originalName).toLowerCase();
        return searchTokens.every((token) => name.includes(token));
      })
      .map((item) => {
        const originalName = item.formatted_ins_name?.toString() || "";
        const normalizedName = this.normalizeFormattedName(originalName);

        return {
          formatted_ins_name: normalizedName,
          trading_symbol: item.trading_symbol?.toString() || "",
          exch: item.exch?.toString() || "",
          token: item.token?.toString() || "",
          expiry: item.expiry_date?.toString() || "",
          lot_size: item.lot_size?.toString() || "",
          tick_size: item.tick_size?.toString() || "",
          strike_price: item.strike_price?.toString() || "",
          segment_type: item.exchange_segment?.toString() || "",
          symbol_name: [
            "BANKNIFTY",
            "FINNIFTY",
            "MIDCPNIFTY",
            "NIFTYNXT50",
            "NIFTY",
          ].includes(normalizedName)
            ? normalizedName
            : item.symbol?.toString() || "",
          code: item.token?.toString() || "", // Adding code for watchlist compatibility
        };
      });

    // Enhanced sort logic with priority order: nse_idx > NSE > BSE > NFO futures > NFO options > MCX
    symbols.sort((a, b) => {
      const nameA = a.formatted_ins_name.toLowerCase();
      const nameB = b.formatted_ins_name.toLowerCase();

      const getMatchScore = (name) => {
        let score = 0;
        for (const token of searchTokens) {
          if (name.startsWith(token)) {
            score += 0;
          } else if (name.includes(token)) {
            score += 1;
          } else {
            score += 2;
          }
        }
        return score;
      };

      const matchScoreA = getMatchScore(nameA);
      const matchScoreB = getMatchScore(nameB);

      if (matchScoreA !== matchScoreB) {
        return matchScoreA - matchScoreB;
      }

      // Priority 1: nse_idx (highest priority) - check both segment_type and exchange_segment
      const isNseIdxA = a.segment_type === "nse_idx";
      const isNseIdxB = b.segment_type === "nse_idx";
      if (isNseIdxA !== isNseIdxB) return isNseIdxA ? -1 : 1;

      // Priority 2: Exchange priority (NSE > BSE > NFO > MCX > others)
      const exchA = a.exch || "";
      const exchB = b.exch || "";
      
      const getExchangePriority = (exch, segmentType) => {
        // nse_idx is already handled above, so this is for other symbols
        if (exch === "NSE") return 1;
        if (exch === "BSE") return 2;
        if (exch === "NFO") {
          // For NFO, prioritize futures over options
          if (segmentType === "nse_fo" || segmentType === "nfo_fut_idx" || segmentType === "nfo_fut") return 3;
          return 4; // NFO options
        }
        if (exch === "MCX") return 5;
        return 6; // Other exchanges
      };

      const exchPriorityA = getExchangePriority(exchA, a.segment_type);
      const exchPriorityB = getExchangePriority(exchB, b.segment_type);
      
      if (exchPriorityA !== exchPriorityB) {
        return exchPriorityA - exchPriorityB;
      }

      // Priority 3: For NFO instruments, prioritize futures (strike_price = "-1") over options
      if (exchA === "NFO" && exchB === "NFO") {
        const isFuturesA = a.strike_price === "-1";
        const isFuturesB = b.strike_price === "-1";
        
        if (isFuturesA !== isFuturesB) {
          return isFuturesA ? -1 : 1; // Futures come first
        }
        
        // Priority 3.1: Exact symbol match priority (e.g., "NIFTY" should come before "NIFTY NXT50")
        const getSymbolMatchPriority = (symbolName, searchTokens) => {
          const lowerSymbol = symbolName.toLowerCase();
          // Check if the symbol exactly matches the first search token
          if (searchTokens.length > 0 && lowerSymbol.startsWith(searchTokens[0])) {
            // If it's an exact match (not containing additional words), give it higher priority
            const words = lowerSymbol.split(/\s+/);
            if (words.length === 1 || words[0] === searchTokens[0]) {
              return 0; // Highest priority for exact matches
            }
            return 1; // Medium priority for partial matches
          }
          return 2; // Lower priority for other matches
        };

        const symbolPriorityA = getSymbolMatchPriority(nameA, searchTokens);
        const symbolPriorityB = getSymbolMatchPriority(nameB, searchTokens);
        
        if (symbolPriorityA !== symbolPriorityB) {
          return symbolPriorityA - symbolPriorityB;
        }
        
        // Priority 3.2: If both are futures or both are options, sort by expiry date (nearest first)
        const expiryA = new Date(parseInt(a.expiry) || 0);
        const expiryB = new Date(parseInt(b.expiry) || 0);
        const expiryComparison = expiryA.getTime() - expiryB.getTime();
        if (expiryComparison !== 0) return expiryComparison;
        
        // If expiry dates are the same, for options sort by strike price
        if (!isFuturesA && !isFuturesB) {
          const strikeA = parseFloat(a.strike_price);
          const strikeB = parseFloat(b.strike_price);
          return strikeA - strikeB;
        }
      }

      // Priority 4: For other instruments, sort by expiry date first, then strike price
      const expiryA = new Date(parseInt(a.expiry) || 0);
      const expiryB = new Date(parseInt(b.expiry) || 0);
      const expiryComparison = expiryA.getTime() - expiryB.getTime();
      if (expiryComparison !== 0) return expiryComparison;

      // If expiry dates are the same, sort by strike price (treat -1 as 0 for futures)
      const strikeA = parseFloat(a.strike_price);
      const strikeB = parseFloat(b.strike_price);
      const normalizedStrikeA = strikeA === -1 ? 0 : strikeA;
      const normalizedStrikeB = strikeB === -1 ? 0 : strikeB;
      return normalizedStrikeA - normalizedStrikeB;
    });
    // console.log(symbols);
    return symbols;
  }

  // Initialize the service (call this when your app starts)
  async initialize() {
    try {
      await this.loadCombinedData();
      return true;
    } catch (error) {
      console.error("Failed to initialize symbol search service:", error);
      return false;
    }
  }

  // Get cache status for debugging
  async getCacheStatus() {
    const exchanges = Object.keys(this.urls);
    const status = {};

    try {
      const db = await this._initDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('exchange');

      for (const exchange of exchanges) {
        try {
          const request = index.getAll(exchange);
          
          await new Promise((resolve, reject) => {
            request.onsuccess = () => {
              const results = request.result;
              if (results.length === 0) {
                status[exchange] = { error: "No cached data" };
              } else {
                // Check if any data is outdated
                const hasOutdatedData = results.some(result => !this._isToday(result.lastUpdated));
                if (hasOutdatedData) {
                  status[exchange] = { error: "Data is outdated" };
                } else {
                  // Merge all data from different sources
                  let totalRecords = 0;
                  const sources = [];
                  results.forEach(result => {
                    totalRecords += result.recordCount || 0;
                    sources.push({
                      source: result.source || 'unknown',
                      recordCount: result.recordCount || 0,
                      lastUpdated: result.lastUpdated
                    });
                  });
                  
                  status[exchange] = {
                    sources: sources,
                    totalRecords: totalRecords,
                    isToday: true,
                    sourceCount: results.length
                  };
                }
              }
              resolve();
            };
            
            request.onerror = () => {
              status[exchange] = { error: "Failed to read data" };
              resolve();
            };
          });
        } catch (error) {
          status[exchange] = { error: "Failed to read data" };
        }
      }
    } catch (error) {
      console.error("Error getting cache status:", error);
      exchanges.forEach((exchange) => {
        status[exchange] = { error: "Database error" };
      });
    }

    return status;
  }

  // Clear all cached data
  async clearCache() {
    try {
      const db = await this._initDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const request = store.clear();
      
      await new Promise((resolve, reject) => {
        request.onsuccess = () => {
          this.allSymbolData = [];
          this.isInitialized = false;
          // console.log("All cached data cleared from IndexedDB");
          resolve();
        };
        
        request.onerror = () => {
          console.error("Error clearing cache:", request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error("Error clearing cache:", error);
      throw error;
    }
  }

  // Debug method to show detailed cache information
  async debugCache() {
    try {
      const db = await this._initDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      const request = store.getAll();
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const results = request.result;
          // console.log("=== CACHE DEBUG INFO ===");
          // console.log(`Total records in cache: ${results.length}`);
          
          const exchangeGroups = {};
          results.forEach(record => {
            if (!exchangeGroups[record.exchange]) {
              exchangeGroups[record.exchange] = [];
            }
            exchangeGroups[record.exchange].push({
              key: record.key,
              source: record.source,
              recordCount: record.recordCount,
              lastUpdated: record.lastUpdated,
              isToday: this._isToday(record.lastUpdated)
            });
          });
          
          Object.keys(exchangeGroups).forEach(exchange => {
            // console.log(`\n${exchange}:`);
            exchangeGroups[exchange].forEach(entry => {
              // console.log(`  - ${entry.key} (${entry.source}): ${entry.recordCount} records, ${entry.isToday ? 'today' : 'outdated'}`);
            });
          });
          
          resolve(exchangeGroups);
        };
        
        request.onerror = () => {
          console.error("Error reading cache for debug:", request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error("Error debugging cache:", error);
      throw error;
    }
  }
}

// Create a singleton instance
const symbolSearchService = new SymbolSearchService();

export default symbolSearchService;
